import { AfterViewInit, Component, EventEmitter, Input, OnDestroy, Output, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';

import { TwcAutocompleteComponent } from '@topcon/twc-angular';

/**
 * Changes behaviour of the autocomplete component to mimic the angularjs material md-chips
 *
 * Changes:
 *  * Hide autocomplete menu
 *  * Hide search action button
 *  * Align input clear button
 *  * Sets the input placeholder
 *  * Space, Comma and Semicolon keys adds elements like Enter key
 *  * On blur acts like Enter key
 *
 *  TODO: This component calls internal TwcAutocompleteComponent members that can vary, is recomended to ask TWC to create a similar one
 */
@Component({
    selector: 'cw-chips-input',
    templateUrl: './chips-input.component.html',
    styleUrls: ['./chips-input.component.scss'],
    standalone: false,
})
export class ChipsInputComponent implements AfterViewInit, OnDestroy {
    @ViewChild(TwcAutocompleteComponent, { static: false }) autocomplete!: TwcAutocompleteComponent;
    private blurEventListener!: EventListenerOrEventListenerObject;

    errorMsg: string | null = null;

    /** Chips list changes event */
    @Output() chipsChange = new EventEmitter<string[]>();

    /** List of chips */
    @Input() chips: string[] = [];

    /** Placeholder to show in the input */
    @Input() placeholder = '';

    /** Helper text shown below the input */
    @Input() hintMsg = '';

    /** Input type: text or number */
    @Input() inputType: 'text' | 'number' = 'text';

    /**  Maximum number of value allowed. If undefined, you can add as many values as you want*/
    @Input() maxValues: number | undefined;

    /** Function to validate the chips values added: If error: Returns the error msg. If valid: return null*/
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    @Input() valueValidation = (value: string | undefined): string | null => null;

    ngAfterViewInit(): void {
        this.initChipsComponent();
    }

    ngOnDestroy(): void {
        if (this.autocompleteInput && this.blurEventListener) {
            this.autocompleteInput.removeEventListener('blur', this.blurEventListener);
        }
    }

    onChanges(changes: string[]): void {
        this.chipsChange.emit(changes);
        if (!this.autocompleteInput) {
            return;
        }
        if (this.maxValues !== undefined && this.chips.length >= this.maxValues) {
            this.autocompleteInput.style.display = 'none';
        } else {
            this.autocompleteInput.style.display = 'inline';
        }
    }

    /**
     * Adds some extra features to autocomplete component
     * * Sets the input placeholder
     * * Space, Comma and Semicolon keys adds elements like Enter key
     * * On blur acts like Enter key
     */
    private initChipsComponent(): void {
        if (!this.autocompleteInput) {
            return;
        }

        const actionKeys = ['Space', 'Comma', 'Enter', 'NumpadEnter', ' ', ';', ','];

        this.autocompleteInput.placeholder = this.placeholder;
        this.autocompleteInput.type = this.inputType;

        if (this.inputType === 'number') {
            this.autocompleteInput.style.maxWidth = '10rem';
            this.autocompleteInput.step = '0.0001';
        }

        this.blurEventListener = (): void => {
            if (!self.validateValue()) {
                return;
            }
            (this.autocomplete as any).handleEnterKeyAction();
        };
        this.autocompleteInput.addEventListener('blur', this.blurEventListener);

        const isActionKey = (this.autocomplete as any).isPreventDefaultNeeded.bind(this.autocomplete);
        (this.autocomplete as any).isPreventDefaultNeeded = function (this: TwcAutocompleteComponent, code: string): boolean {
            if (actionKeys.includes(code)) {
                return isActionKey('Enter');
            }

            return isActionKey(code);
        }.bind(this.autocomplete);

        const handleKeyAction = (this.autocomplete as any).handleKeyAction.bind(this.autocomplete);

        // eslint-disable-next-line @typescript-eslint/no-this-alias
        const self = this;
        (this.autocomplete as any).handleKeyAction = function (this: TwcAutocompleteComponent, code: string, isEmpty: boolean): void {
            if (!self.autocompleteInput) {
                return;
            }
            const control: FormControl = self.autocomplete.control;
            const trimmedControlValue = self.trimValue(control.getRawValue());
            control.setValue(trimmedControlValue);
            if (self.valueValidation(trimmedControlValue) === null) {
                self.errorMsg = null;
            }
            if (actionKeys.includes(code)) {
                if (!self.validateValue()) {
                    return;
                }
                handleKeyAction('Enter', false);
                return;
            }
            try {
                handleKeyAction(code, isEmpty);
            } catch {
                // silent catching options not present error
            }
        }.bind(this.autocomplete);
    }

    private trimValue(value: string): string {
        return value.replace(/[ ;,\s]+$/, '');
    }

    private validateValue(): boolean {
        if (!this.autocompleteInput?.value) {
            return false;
        }
        this.errorMsg = this.valueValidation(this.autocompleteInput.value);
        if (this.errorMsg) {
            return false;
        }

        if (this.chips.includes(this.autocompleteInput.value)) {
            // Avoid duplicated values
            return false;
        }
        return true;
    }

    private get autocompleteInput(): HTMLInputElement | undefined {
        return this.autocomplete.inputRef?.nativeElement;
    }
}
